import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowsClockwise,
  ChartBar,
  Copy,
  LinkSimple,
  PencilSimple,
  Trash,
} from '@phosphor-icons/react';
import { api } from '@/lib/api';
import { apiErrorMessage, fmtDate } from '@/lib/utils';
import { Badge, Button, EmptyState, Input, Label, Surface, Textarea, useToast } from '@/components/ui-kit';

const EMPTY_FORM = {
  name: '',
  destinationUrl: 'https://wa.link/omyngp',
  slug: '',
  campaign: '',
  source: '',
  group: '',
  lead: '',
  utmSource: 'groups',
  utmMedium: 'whatsapp',
  utmCampaign: '',
  utmContent: '',
  isActive: true,
};

function normalizeSlug(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

function hostLabel(url) {
  try {
    return new URL(url).host;
  } catch {
    return url || '-';
  }
}

export default function LinksPage() {
  const [links, setLinks] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [clicks, setClicks] = useState({ clicks: [], bySource: [] });
  const [loadingClicks, setLoadingClicks] = useState(false);
  const toast = useToast();

  const currentLink = useMemo(
    () => links.find((link) => link.id === selected?.id) || selected,
    [links, selected]
  );

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: field === 'slug' ? normalizeSlug(value) : value }));
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const rows = await api.listShortLinks();
      setLinks(rows);
      if (!selected && rows.length) setSelected(rows[0]);
    } catch (error) {
      toast.show(apiErrorMessage(error, 'Erro ao carregar links'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const refreshClicks = async (link = currentLink) => {
    if (!link?.id) return;
    setLoadingClicks(true);
    try {
      setClicks(await api.shortLinkClicks(link.id, 120));
    } catch (error) {
      toast.show(apiErrorMessage(error, 'Erro ao carregar cliques'), 'error');
    } finally {
      setLoadingClicks(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshClicks(currentLink);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLink?.id]);

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const editLink = (link) => {
    setEditingId(link.id);
    setForm({
      name: link.name || '',
      destinationUrl: link.destinationUrl || '',
      slug: link.slug || '',
      campaign: link.campaign || '',
      source: link.source || '',
      group: link.group || '',
      lead: link.lead || '',
      utmSource: link.utmSource || 'groups',
      utmMedium: link.utmMedium || 'whatsapp',
      utmCampaign: link.utmCampaign || '',
      utmContent: link.utmContent || '',
      isActive: link.isActive !== false,
    });
    setSelected(link);
  };

  const saveLink = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        destinationUrl: form.destinationUrl.trim(),
        slug: form.slug.trim(),
      };
      const saved = editingId
        ? await api.updateShortLink(editingId, payload)
        : await api.createShortLink(payload);
      toast.show(editingId ? 'Link atualizado.' : 'Link curto criado.', 'success');
      setSelected(saved);
      resetForm();
      await refresh();
    } catch (error) {
      toast.show(apiErrorMessage(error, 'Erro ao salvar link'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteLink = async (link) => {
    if (!window.confirm(`Excluir o link ${link.slug}?`)) return;
    try {
      await api.deleteShortLink(link.id);
      toast.show('Link removido.', 'success');
      if (selected?.id === link.id) setSelected(null);
      await refresh();
    } catch (error) {
      toast.show(apiErrorMessage(error, 'Erro ao excluir link'), 'error');
    }
  };

  const copyLink = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.show('Link copiado.', 'success');
    } catch {
      toast.show(value, 'info');
    }
  };

  return (
    <div data-testid="page-links" className="space-y-7">
      {toast.node}
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="mb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-lime-500">
            // Rastreamento
          </div>
          <h1 className="font-display text-4xl font-black tracking-tighter text-zinc-50">
            Links curtos
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-zinc-400">
            Gere links curtos com UTMs escondidas e acompanhe cliques por campanha, origem, grupo e lead.
          </p>
        </div>
        <Button variant="secondary" onClick={refresh}>
          <ArrowsClockwise size={15} className={loading ? 'animate-spin' : ''} />
          Atualizar
        </Button>
      </div>

      <Surface className="p-5">
        <form onSubmit={saveLink} className="grid gap-4 lg:grid-cols-12">
          <div className="lg:col-span-3">
            <Label>Nome interno</Label>
            <Input value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Venda Clube do Glow" />
          </div>
          <div className="lg:col-span-5">
            <Label>URL real</Label>
            <Input
              required
              value={form.destinationUrl}
              onChange={(e) => setField('destinationUrl', e.target.value)}
              placeholder="https://wa.link/omyngp"
            />
          </div>
          <div className="lg:col-span-3">
            <Label>Slug editável</Label>
            <Input value={form.slug} onChange={(e) => setField('slug', e.target.value)} placeholder="gerado automaticamente" />
          </div>
          <div className="flex items-end lg:col-span-1">
            <Button type="submit" disabled={saving} className="w-full">
              {saving ? '...' : editingId ? 'Salvar' : 'Criar'}
            </Button>
          </div>

          <div className="lg:col-span-3">
            <Label>Campanha</Label>
            <Input value={form.campaign} onChange={(e) => setField('campaign', e.target.value)} placeholder="priscila-clubedoglow" />
          </div>
          <div className="lg:col-span-3">
            <Label>Origem</Label>
            <Input value={form.source} onChange={(e) => setField('source', e.target.value)} placeholder="conv1" />
          </div>
          <div className="lg:col-span-3">
            <Label>Grupo</Label>
            <Input value={form.group} onChange={(e) => setField('group', e.target.value)} placeholder="#10 clube do glow" />
          </div>
          <div className="lg:col-span-3">
            <Label>Lead</Label>
            <Input value={form.lead} onChange={(e) => setField('lead', e.target.value)} placeholder="telefone ou id" />
          </div>

          <details className="lg:col-span-12 rounded-sm border border-zinc-800 bg-zinc-950/60 px-4 py-3">
            <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-[0.16em] text-zinc-400">
              UTMs padrão
            </summary>
            <div className="mt-4 grid gap-4 md:grid-cols-4">
              <div>
                <Label>utm_source</Label>
                <Input value={form.utmSource} onChange={(e) => setField('utmSource', e.target.value)} />
              </div>
              <div>
                <Label>utm_medium</Label>
                <Input value={form.utmMedium} onChange={(e) => setField('utmMedium', e.target.value)} />
              </div>
              <div>
                <Label>utm_campaign</Label>
                <Input value={form.utmCampaign} onChange={(e) => setField('utmCampaign', e.target.value)} placeholder="usa campanha/slug" />
              </div>
              <div>
                <Label>utm_content</Label>
                <Input value={form.utmContent} onChange={(e) => setField('utmContent', e.target.value)} placeholder="usa origem" />
              </div>
            </div>
          </details>

          {editingId && (
            <div className="lg:col-span-12">
              <Button type="button" variant="ghost" onClick={resetForm}>
                Cancelar edição
              </Button>
            </div>
          )}
        </form>
      </Surface>

      <div className="grid gap-6 xl:grid-cols-[1fr_420px]">
        <Surface className="p-5">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="font-display text-xl font-bold text-zinc-50">Links criados</h2>
              <p className="text-xs text-zinc-500">Clique em um link para ver os detalhes de rastreio.</p>
            </div>
            <Badge variant="info">{links.length} links</Badge>
          </div>
          {!links.length ? (
            <EmptyState icon={LinkSimple} title="Nenhum link ainda" hint="Crie o primeiro link curto para rastrear cliques de venda." />
          ) : (
            <div className="space-y-3">
              {links.map((link) => (
                <div
                  key={link.id}
                  onClick={() => setSelected(link)}
                  className="grid w-full cursor-pointer gap-3 rounded-sm border border-zinc-800 bg-zinc-950/70 p-4 text-left transition-colors hover:border-lime-500/40 lg:grid-cols-[1fr_140px_132px]"
                  data-testid={`short-link-${link.slug}`}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-zinc-100">{link.name || link.slug}</span>
                      <Badge variant={link.isActive === false ? 'danger' : 'success'}>
                        {link.isActive === false ? 'inativo' : 'ativo'}
                      </Badge>
                    </div>
                    <p className="mt-1 truncate font-mono text-xs text-lime-300">{link.publicUrl}</p>
                    <p className="mt-1 truncate text-xs text-zinc-500">Destino: {hostLabel(link.destinationUrl)}</p>
                  </div>
                  <div className="font-mono text-xs text-zinc-400">
                    <div>campanha: {link.campaign || '-'}</div>
                    <div>origem: {link.source || '-'}</div>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <Badge variant="info">{link.clicks || 0} cliques</Badge>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        copyLink(link.publicUrl);
                      }}
                      className="grid h-8 w-8 place-items-center rounded-sm border border-zinc-700 text-zinc-300 hover:text-lime-300"
                    >
                      <Copy size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        editLink(link);
                      }}
                      className="grid h-8 w-8 place-items-center rounded-sm border border-zinc-700 text-zinc-300 hover:text-lime-300"
                    >
                      <PencilSimple size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        deleteLink(link);
                      }}
                      className="grid h-8 w-8 place-items-center rounded-sm border border-red-500/30 text-red-300 hover:bg-red-500/10"
                    >
                      <Trash size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Surface>

        <Surface className="p-5">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="font-display text-xl font-bold text-zinc-50">Rastreio</h2>
              <p className="text-xs text-zinc-500">{currentLink?.slug || 'Selecione um link'}</p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => refreshClicks(currentLink)} disabled={!currentLink}>
              <ChartBar size={14} className={loadingClicks ? 'animate-pulse' : ''} />
              Ver cliques
            </Button>
          </div>

          {currentLink ? (
            <div className="space-y-5">
              <div className="rounded-sm border border-zinc-800 bg-zinc-950/70 p-4">
                <Label>Link curto</Label>
                <div className="flex gap-2">
                  <Input readOnly value={currentLink.publicUrl || ''} />
                  <Button type="button" variant="secondary" onClick={() => copyLink(currentLink.publicUrl)}>
                    <Copy size={14} />
                  </Button>
                </div>
              </div>
              <div>
                <Label>Origem dos cliques</Label>
                <div className="space-y-2">
                  {(clicks.bySource || []).slice(0, 8).map((row) => (
                    <div key={row._id || 'direct'} className="flex items-center justify-between rounded-sm border border-zinc-800 px-3 py-2 font-mono text-xs">
                      <span className="text-zinc-300">{row._id || 'direct'}</span>
                      <span className="text-lime-300">{row.clicks} cliques</span>
                    </div>
                  ))}
                  {!clicks.bySource?.length && <p className="text-sm text-zinc-500">Sem cliques registrados ainda.</p>}
                </div>
              </div>
              <div>
                <Label>Cliques recentes</Label>
                <div className="max-h-[360px] space-y-2 overflow-y-auto pr-1">
                  {(clicks.clicks || []).map((click) => (
                    <div key={click.id} className="rounded-sm border border-zinc-800 bg-zinc-950/70 p-3">
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-mono text-xs text-zinc-400">{fmtDate(click.createdAt)}</span>
                        <Badge variant="default">{click.source || 'direct'}</Badge>
                      </div>
                      <Textarea
                        readOnly
                        className="mt-2 min-h-[72px] text-xs"
                        value={[
                          `campanha: ${click.campaign || '-'}`,
                          `grupo: ${click.group || '-'}`,
                          `lead: ${click.lead || '-'}`,
                          `url final: ${click.finalUrl || '-'}`,
                        ].join('\n')}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <EmptyState icon={LinkSimple} title="Selecione um link" hint="Os cliques aparecerão aqui em tempo real após o primeiro acesso." />
          )}
        </Surface>
      </div>
    </div>
  );
}
